import { makeAutoObservable, runInAction } from 'mobx';
import type { FHRecurringInfo } from '../types';
import { recurringApi } from '../api/recurring_api';
import type { CreateRecurringParams, EditRecurringParams } from '../api/recurring_api';

export class RecurringStore {
  recurrings: Map<string, FHRecurringInfo> = new Map();
  loading = false;
  cursor: string | null = null;
  hasMore = true;
  error: string | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  get allRecurrings(): FHRecurringInfo[] {
    return Array.from(this.recurrings.values()).sort(
      (a, b) => {
        const dateA = a.date_created ? new Date(a.date_created).getTime() : 0;
        const dateB = b.date_created ? new Date(b.date_created).getTime() : 0;
        return dateB - dateA;
      }
    );
  }

  async fetchRecurrings(refresh = false) {
    if (this.loading) return;
    if (!refresh && !this.hasMore) return;

    this.loading = true;
    this.error = null;

    try {
      const cursor = refresh ? undefined : (this.cursor || undefined);
      const response = await recurringApi.list(cursor);
      runInAction(() => {
        if (refresh) {
          this.recurrings.clear();
        }
        for (const rec of response.items) {
          this.recurrings.set(rec.key, rec);
        }
        this.cursor = response.cursor || null;
        this.hasMore = !!response.cursor;
        this.loading = false;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : 'Failed to load recurring requests';
        this.loading = false;
      });
    }
  }

  async createRecurring(params: CreateRecurringParams) {
    this.error = null;
    try {
      const response = await recurringApi.create(params);
      runInAction(() => {
        this.recurrings.set(response.recurring.key, response.recurring);
      });
      return response;
    } catch (err) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : 'Failed to create recurring request';
      });
      return null;
    }
  }

  async editRecurring(key: string, params: EditRecurringParams) {
    try {
      const updated = await recurringApi.edit(key, params);
      runInAction(() => {
        this.recurrings.set(updated.key, updated);
      });
      return updated;
    } catch (err) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : 'Failed to edit recurring request';
      });
      return null;
    }
  }

  async skipRecurring(key: string) {
    try {
      const updated = await recurringApi.skip(key);
      runInAction(() => { this.recurrings.set(updated.key, updated); });
      return updated;
    } catch (err) {
      runInAction(() => { this.error = err instanceof Error ? err.message : 'Failed to skip'; });
      return null;
    }
  }

  async unskipRecurring(key: string) {
    try {
      const updated = await recurringApi.unskip(key);
      runInAction(() => { this.recurrings.set(updated.key, updated); });
      return updated;
    } catch (err) {
      runInAction(() => { this.error = err instanceof Error ? err.message : 'Failed to unskip'; });
      return null;
    }
  }

  async stopRecurring(key: string) {
    try {
      const updated = await recurringApi.stop(key);
      runInAction(() => { this.recurrings.set(updated.key, updated); });
      return updated;
    } catch (err) {
      runInAction(() => { this.error = err instanceof Error ? err.message : 'Failed to stop'; });
      return null;
    }
  }

  async resumeRecurring(key: string) {
    try {
      const updated = await recurringApi.resume(key);
      runInAction(() => { this.recurrings.set(updated.key, updated); });
      return updated;
    } catch (err) {
      runInAction(() => { this.error = err instanceof Error ? err.message : 'Failed to resume'; });
      return null;
    }
  }
}

export const recurringStore = new RecurringStore();
